import { ColDef } from 'ag-grid-community';
import React, { FC, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Grid } from 'components/atoms/grid';
import { useGrid } from 'components/organisms/timesheets/drawers/calendar-integration/add-activity-page/activity-table';
import { TemplateObjectType } from 'data/templates/api';
import { useTranslate } from 'hooks/use-translation';
import { TemplateListProp } from '../types';
import {
  getAvatar,
  getDate,
  getTemplateName,
  getUserName,
  NameType,
  NoResultTemplateListEmptyState,
  TemplateOptionsRenderer,
} from './table-components';
import {
  userNameSorter,
  templateNameSorter,
  dateSorter,
  getPrivateRowStyles,
} from './table-utils';

const GridContainer = styled.div`
  height: calc(100vh - var(--spacing-12));

  .ag-row-selected {
    background-color: var(--blue-20) !important;
  }
`;

const StyledGrid = styled(Grid)`
  height: 100%;
  margin-bottom: var(--spacing-06);

  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
  }
  .ag-row {
    border-color: var(--table-border-color);
  }
`;

export const TemplateTableList: FC<{
  templateData: TemplateListProp[];
  objectType: TemplateObjectType;
  onDelete: (id, objectType: TemplateObjectType) => void;
  onDuplicate?: (id, objectType: TemplateObjectType) => void;
  route: string;
}> = ({ templateData, objectType, onDelete, onDuplicate, route }) => {
  const { onGridReady, gridApi } = useGrid();
  const translate = useTranslate();
  const { push } = useHistory();

  const onFirstDataRendered = useCallback(
    () => gridApi?.sizeColumnsToFit(),
    [gridApi],
  );

  const getRowStyles = (params) => getPrivateRowStyles(objectType, params);

  const defaultColDef = useMemo(() => {
    return {
      filter: false,
      sortable: true,
      resizable: true,
      wrapText: true,
      suppressMovable: true,
      suppressMenu: true,
      editable: false,
      rowDrag: false,
      unSortIcon: true,
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    const onCellCliked = ({ data }) => {
      push(`${route}/${data.templateId}`);
    };

    return [
      {
        width: 50,
        headerName: '',
        cellRenderer: () => getAvatar(objectType),
        field: 'user',
        sortable: false,
      },
      {
        width: 300,
        headerName: translate('template.table.header.name'),
        cellRenderer: getTemplateName,
        field: 'templateName',
        comparator: templateNameSorter,
        onCellClicked: onCellCliked,
        cellRendererParams: {
          translate,
          route,
        },
        cellStyle: { cursor: 'pointer' },
      },
      {
        width: 150,
        headerName: translate('template.table.header.last.updated.by'),
        cellRenderer: getUserName,
        field: 'publishedBy',
        comparator: userNameSorter,
        cellRendererParams: {
          translate,
          type: NameType.PublishedBy,
        },
      },
      {
        width: 150,
        headerName: translate('template.table.header.last.updated.on'),
        cellRenderer: getDate,
        field: 'publishedAt',
        comparator: dateSorter,
        cellRendererParams: {
          translate,
          type: NameType.PublishedAt,
        },
      },
      {
        width: 150,
        headerName: translate('template.table.header.created.by'),
        cellRenderer: getUserName,
        field: 'createdBy',
        comparator: userNameSorter,
        cellRendererParams: {
          translate,
          type: NameType.CreatedBy,
        },
      },
      {
        width: 150,
        headerName: translate('template.table.header.created.on'),
        cellRenderer: getDate,
        field: 'createdAt',
        comparator: dateSorter,
        cellRendererParams: {
          translate,
          type: NameType.CreatedAt,
        },
      },

      {
        width: 50,
        sortable: false,
        cellRenderer: 'TemplateOptionsRenderer',
        cellRendererParams: {
          objectType,
          onDelete,
          onDuplicate,
        },
      },
    ];
  }, [objectType, onDelete, onDuplicate, push, route, translate]);

  return (
    <GridContainer className="ag-theme-balham">
      <StyledGrid
        className="ag-theme-balham"
        animateRows
        getRowStyle={getRowStyles}
        suppressContextMenu
        suppressRowClickSelection
        suppressCellSelection
        suppressHorizontalScroll
        onGridReady={onGridReady}
        noRowsOverlayComponent="NoResultTemplateListEmptyState"
        frameworkComponents={{
          TemplateOptionsRenderer,
          NoResultTemplateListEmptyState,
        }}
        rowData={templateData}
        columnDefs={columnDefs}
        size="large"
        immutableData
        defaultColDef={defaultColDef}
        onFirstDataRendered={onFirstDataRendered}
        headerHeight={32}
        rowHeight={48}
        getRowNodeId={(d) => d.templateId}
        getDataPath={(d) => [d.templateId]}
      />
    </GridContainer>
  );
};
